import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { APP_VERSION } from "../constants/appVersion";

const utahData = "/img/UTDT_Logo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

export default function MiningTable20() {
  const pdfRef = useRef();

  const data = [
    {
      industry_label: "Support Activities for Mining",
      2018: 135,
      2019: 118,
      2020: 102,
      2021: 118,
      2022: 133,
      2023: 158,
      2024: 168,
    },
    {
      industry_label: "Oil and Gas Extraction",
      2018: 102,
      2019: 98,
      2020: 72,
      2021: 70,
      2022: 75,
      2023: 91,
      2024: 97,
    },
    {
      industry_label: "Metal Ore Mining",
      2018: 36,
      2019: 12,
      2020: 35,
      2021: 50,
      2022: 56,
      2023: 64,
      2024: 80,
    },
    {
      industry_label: "Nonmetallic Mineral Mining and Quarrying",
      2018: 27,
      2019: 21,
      2020: 21,
      2021: 22,
      2022: 20,
      2023: 26,
      2024: 24,
    },
    {
      industry_label: "Coal Mining",
      2018: 4,
      2019: 4,
      2020: 7,
      2021: 8,
      2022: 9,
      2023: 0,
      2024: 0,
    },
  ];

  const years = ["2018", "2019", "2020", "2021", "2022", "2023", "2024"];
  const colors = ["#935116", "#EC4899", "#10B981", "#FBBF24", "#A78BFA"];

  // Chart Data
  const chartData = {
    labels: years,
    datasets: data.map((d, i) => ({
      label: d.industry_label,
      data: years.map((y) => d[y]),
      borderColor: colors[i % colors.length],
      backgroundColor: colors[i % colors.length],
      borderWidth: 2.5,
      tension: 0.35,
      pointRadius: 4,
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" },
      title: {
        display: true,
        text: "Employment Trend of American Indian & Alaska Native (2018-2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Year" },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        title: { display: true, text: "Employment Count" },
      },
    },
  };

  // Summary Insights
  const total2018 = data.reduce((sum, d) => sum + d[2018], 0);
  const total2024 = data.reduce((sum, d) => sum + d[2024], 0);
  const overallGrowth = (((total2024 - total2018) / total2018) * 100).toFixed(
    1,
  );

  const growthData = data.map((d) => {
    const growth = ((d[2024] - d[2018]) / d[2018]) * 100;
    return {
      industry: d.industry_label,
      growth: growth.toFixed(1),
      latest: d[2024],
    };
  });

  const topGrowth = growthData.reduce(
    (max, g) => (parseFloat(g.growth) > parseFloat(max.growth) ? g : max),
    growthData[0],
  );

  const topIndustry2024 = growthData.reduce(
    (max, g) => (g.latest > max.latest ? g : max),
    growthData[0],
  );

  // PDF Download
  const downloadPDF = () => {
    const input = pdfRef.current;
    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      const addHeader = () => {
        const logoWidth = 20; // ideal header width
        const logoHeight = 20; // adjust ratio if needed

        pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY,
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right",
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // Add more pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Final footer
      // ===== FINAL FOOTER SAFE AREA =====

      const footerTopY = pageHeight - 40; // start footer 40mm from bottom
      const footerContentY = pageHeight - 28; // logos row
      const footerBottomTextY = pageHeight - 10; // bottom text row

      // Top Divider Line
      pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

      // UtahData logo
      const utahWidth = 18;
      const utahHeight = 18;
      pdf.addImage(
        utahData,
        "PNG",
        20,
        footerContentY - 8,
        utahWidth,
        utahHeight,
      );

      // Built by
      pdf.setFontSize(10);
      pdf.text("Built by", 45, footerContentY);

      // BODEX logo
      const bodexWidth = 36;
      const bodexHeight = bodexWidth * 0.3;
      pdf.addImage(
        bodex,
        "PNG",
        60,
        footerContentY - 6,
        bodexWidth,
        bodexHeight,
      );

      // Using text
      pdf.text("using", 102, footerContentY);

      // KPI logo
      pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

      // Version
      pdf.text(APP_VERSION, 140, footerContentY);

      // Bottom Info Line
      pdf.setFontSize(8);
      pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
      pdf.text(
        `Downloaded on: ${new Date().toLocaleDateString()}`,
        pageWidth - 10,
        footerBottomTextY,
        null,
        null,
        "right",
      );
      pdf.save("employment_trend_mining_industries.pdf");
    });
  };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-blue-50 via-white to-pink-50 rounded-xl shadow-md"
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Employment Trend of American Indian & Alaska Native (2018-2024)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Overall Industry Growth
            </p>
            <p className="text-xl font-bold text-blue-800">+{overallGrowth}%</p>
          </div>
          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Highest Growth Industry
            </p>
            <p className="text-sm font-semibold text-yellow-800">
              {topGrowth.industry}
            </p>
            <p className="text-lg font-bold text-yellow-800">
              +{topGrowth.growth}%
            </p>
          </div>
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Top Employment Industry (2024)
            </p>
            <p className="text-sm font-semibold text-green-800">
              {topIndustry2024.industry}
            </p>
            <p className="text-lg font-bold text-green-800">
              {topIndustry2024.latest.toLocaleString("en-US")}
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          {/* <div className="flex flex-col h-full"> */}
          <div className="overflow-x-auto flex items-center">
            <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Industry</th>
                  {years.map((y) => (
                    <th key={y} className="p-2">
                      {y}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr
                    key={i}
                    className={`${
                      row.industry_label === topIndustry2024.industry
                        ? "bg-green-100 font-bold"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    <td className="p-2 text-left">{row.industry_label}</td>
                    {years.map((y) => (
                      <td key={y} className="p-2">
                        {row[y].toLocaleString("en-US")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* </div> */}

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Line data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download PDF */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
